"use client";

import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import {
  getVerificationGuideStatus,
  markVerificationGuideSeen,
  type VerificationStatus,
} from "@/app/actions/verification";

// 모달(Lottie 애니메이션 포함, ~315KB)은 로그인 + 본인인증 미완료 사용자에게 한 번 뜨는
// 화면이라 모든 페이지 공용 번들에 둘 이유가 없다 — 처음 열 때 받는다.
const VerificationGuideModal = dynamic(
  () => import("./VerificationGuideModal").then((m) => m.VerificationGuideModal),
  { ssr: false },
);

const EXCLUDED_PATHS = [
  "/login",
  "/signup",
  "/blocked",
  "/auth",
  "/verify-phone",
  "/account-settings/login-security",
  "/profile/security",
];

export function VerificationGuideWrapper() {
  const pathname = usePathname();
  // 로그인 상태는 AuthProvider 에서 — 자체 safeGetUser/onAuthStateChange 구독 대신.
  const { status, user } = useAuth();
  const [open, setOpen] = useState(false);
  // 한 번 열린 뒤에는 닫혀도 마운트를 유지한다 — 닫힘 애니메이션이 잘리지 않게.
  const [everOpened, setEverOpened] = useState(false);
  // 안내 여부를 이미 확인한 사용자 id. 렌더에 쓰지 않으므로 ref — 다른 사용자로
  // 바뀌면(로그아웃 후 재로그인 등) 다시 확인한다.
  const checkedUserIdRef = useRef<string | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<{
    id: VerificationStatus;
    face: VerificationStatus;
    phone: VerificationStatus;
  }>({
    id: "none",
    face: "none",
    phone: "none",
  });

  const userId = status === "authenticated" ? (user?.id ?? null) : null;
  // EULA gate takes priority — if the user hasn't accepted the latest
  // terms yet, the layout-level <TermsAcceptGate> is already covering
  // the screen. Don't pile a verification guide modal on top.
  const needsTermsConsent = !!user && !user.terms_accepted_at;

  useEffect(() => {
    if (!userId || needsTermsConsent) return;
    if (checkedUserIdRef.current === userId) return;

    // Check if current path is excluded
    const isExcludedPath = EXCLUDED_PATHS.some((path) =>
      pathname?.includes(path)
    );
    if (isExcludedPath) return;

    let cancelled = false;
    (async () => {
      // Get verification guide status
      const guide = await getVerificationGuideStatus();
      if (cancelled || !guide) return;

      checkedUserIdRef.current = userId;

      // If guide has been seen, don't show again
      if (guide.hasSeenGuide) return;

      // Check if any verification is incomplete
      const hasIncompleteVerification =
        guide.idStatus !== "approved" ||
        guide.faceStatus !== "approved" ||
        guide.phoneStatus !== "approved";

      if (hasIncompleteVerification) {
        setVerificationStatus({
          id: guide.idStatus,
          face: guide.faceStatus,
          phone: guide.phoneStatus,
        });
        setOpen(true);
        setEverOpened(true);
      }
    })().catch((error) => {
      // 서버 액션 POST 중단(페이지 전환 등) — 다음 경로 변경 때 다시 시도한다.
      console.warn("[VerificationGuideWrapper] status check failed:", error);
    });

    return () => {
      cancelled = true;
    };
  }, [userId, needsTermsConsent, pathname]);

  const handleOpenChange = async (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Mark guide as seen when modal is closed
      await markVerificationGuideSeen();
    }
  };

  const handleContinue = async () => {
    // Mark guide as seen
    await markVerificationGuideSeen();
    setOpen(false);
  };

  if (!everOpened) return null;

  return (
    <VerificationGuideModal
      // 로그아웃되면(userId 없음) 바로 닫힌다 — 종전 SIGNED_OUT 핸들러의 setOpen(false).
      open={open && !!userId}
      onOpenChange={handleOpenChange}
      onContinue={handleContinue}
      verificationStatus={verificationStatus}
    />
  );
}
